import { subscribeToRTM } from '../RTMResolvers';
import {
  RTM_LEARNER_STUDENT_SCOPE_SUBSCRIBE,
  RTM_LEARNER_STUDENT_SCOPE_SUBSCRIBE_OK,
  RTM_LEARNER_STUDENT_SCOPE_SUBSCRIBE_ERROR,
  RTM_LEARNER_STUDENT_SCOPE_UNSUBSCRIBE,
} from './constants';

const subscribeToStateWithRTM = async (deploymentId, listener) => {
  // https://gitlab.com/pearsontechnology/gpt/aero/aero-service-mercury/commit/4807ae0a052dd62406cc0fb042db296c8b985dad
  const message = {
    type: RTM_LEARNER_STUDENT_SCOPE_SUBSCRIBE,
    deploymentId,
  };

  const subscription = await subscribeToRTM(
    message,
    listener,
    RTM_LEARNER_STUDENT_SCOPE_SUBSCRIBE_OK,
    RTM_LEARNER_STUDENT_SCOPE_SUBSCRIBE_ERROR,
  );

  if (!subscription) {
    return () => {};
  }

  const unsubscribeMessage = {
    type: RTM_LEARNER_STUDENT_SCOPE_UNSUBSCRIBE,
    deploymentId,
  };

  return () => subscription.unsubscribe(unsubscribeMessage);
};

/* Manages and proxies subscriptions to the RTM. One subscription to
 * RTM should serve multiple plugin subscriptions.
 *
 * Possible areas for improvement:
 * - Queue messages so they arrive in order
 * - Use a more efficient lookup finding subscriptions based on criteria.
 *   (at present, i don't think this would be called a lot with lots of subscriptions)
 */
export default class SubscriptionAggregation {
  constructor(deploymentId) {
    this.deploymentId = deploymentId;
    this.rtmDeployementSubscription = new Set();
    this.managedSubscriptions = new Map();
    this.rtmUnsubscribe = null;

    this.handleRtmNotification = this.handleRtmNotification.bind(this);
    this.managedUnsubscribe = this.managedUnsubscribe.bind(this);
  }

  /*
   * Build a random subscription id (string) to identify a fresh subscription
   */
  buildSubscriptionId() {
    return [1, 2, 3]
      .map(() => Math.floor(Math.random() * 10000000000))
      .join('-');
  }

  /*
   * Construct an RTM message that subscribes to the entire deployment.
   */
  buildRtmSubscriptionMessage() {
    return {
      type: RTM_LEARNER_STUDENT_SCOPE_SUBSCRIBE,
      deploymentId: this.deploymentId,
    };
  }

  /*
   * Creates our master subscription to the RTM,
   * ensuring there is only one RTM Subscription for deployment
   */
  async subscribeToRtm() {
    if (!this.rtmDeployementSubscription.has(this.deploymentId)) {
      this.rtmDeployementSubscription.add(this.deploymentId);
      this._unsubscribeFromRtm = await subscribeToStateWithRTM(this.deploymentId, this.handleRtmNotification);
    }
  }

  unsubscribeFromRtm() {
    // Perhaps it might be a good idea to unsubscribe from the RTM when subscriptions = 0.
    if (this._unsubscribeFromRtm) {
      this._unsubscribeFromRtm().then(() => {
        this._unsubscribeFromRtm = null;
      });
    }
  }

  handleRtmNotification(message) {
    const { response } = message;
    if (response && response.studentScopeURN) {
      [...this.managedSubscriptions.values()]
        // find each subscription which shares the same studentScopeURN.
        .filter(({ studentScopeURN }) => studentScopeURN === response.studentScopeURN)
        // and notify each one by calling the supplied callback
        .forEach((subscription) => {
          subscription.callback(response);
        });
    }
  }

  /*
   * Creates a new subscription that is actually sharing the existing rtm subscription
   */
  async subscribe(studentScopeURN, callback) {
    await this.subscribeToRtm();

    const newSubscription = {
      id: this.buildSubscriptionId(),
      callback,
      studentScopeURN,
    };

    this.managedSubscriptions.set(newSubscription.id, newSubscription);

    return () => this.managedUnsubscribe(newSubscription.id);
  }

  managedUnsubscribe(id) {
    this.managedSubscriptions.delete(id);
  }
}
